import { ChangeEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { developerApplicationSelector } from '../../../../store';
import Button from '../../../UI/Button';
import CheckboxField from '../../../UI/Form/CheckboxField';
import { majorityOwnership, racialEthinicGroup } from './constant';
import { setLeadershipInformation } from '../../../../store/DeveloperApplication/LeadershipInformation';
import { bodTotalPercent } from './helper';
import { useMutation } from '@tanstack/react-query';
import {
  signDeveloperAsset,
  PPSignedAssetResponse,
} from '../../../../api/Rover/partnerProposal';
import { useFileUpload, UploadFnReturn } from '../../../../hooks/useFileUpload';
import { FileInputField } from '../../../UI/HookForm/Form/File/FileInputField';
import { SignDeveloperAssetPayload } from '../../../../types/Shared';

export default function LeadershipInformation({
  nextHandler,
  previousHandler,
}: {
  nextHandler: () => void;
  previousHandler: () => void;
}) {
  const dispatch = useDispatch();
  const { leadershipInformation } = useSelector(developerApplicationSelector);
  const { boardOfDirectorsPercent } = leadershipInformation;
  const [bodErrors, setBodErrors] = useState('');
  const [formErrors, setFormErrors] = useState('');
  const [uploadError, setUploadError] = useState<string>('');

  const { upload } = useFileUpload({
    signAsset: signDeveloperAsset,
  });

  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: SignDeveloperAssetPayload[];
      files: File[];
      formState: any;
    }) => upload(payload),
    {
      onSuccess: ({ sign, files }: UploadFnReturn, { formState }) => {
        let signRes: PPSignedAssetResponse[] = [];
        if (Array.isArray(sign)) {
          signRes = [...sign];
        } else {
          signRes = [{ ...sign }];
        }

        updateState({
          ...formState,
          principalsResume: signRes.map((sig, index) => ({
            name: files[index].name,
            path: sig.path,
            key: sig.key,
            type: files[index].type,
            field: 'principalsResume',
            description: 'Principal Resume',
          })),
        });
      },
      onError: (error: any) => {
        setUploadError('Failed to upload Document');
      },
    }
  );
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    getValues,
    trigger,
    control,
  } = useForm({
    defaultValues: leadershipInformation,
    mode: 'all',
  });

  const bodInputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const updatedBoardOfDirectorsPercent = {
      ...boardOfDirectorsPercent,
      [e.target.name]: e.target.value,
    };
    if (parseInt(e.target.value) < 0) {
      setBodErrors('Cannot be negative value');
    } else if (isNaN(+e.target.value)) {
      setBodErrors('Please enter a number');
    } else {
      setValue('boardOfDirectorsPercent', updatedBoardOfDirectorsPercent);
      dispatch(
        setLeadershipInformation({
          boardOfDirectorsPercent: updatedBoardOfDirectorsPercent,
        })
      );
      setBodErrors('');
    }
  };

  const checkFormValidity = () => {
    let totalBodPercent = bodTotalPercent(boardOfDirectorsPercent);
    if (totalBodPercent > 100) {
      return false;
    }

    return true;
  };

  const updateState = (data: any) => {
    dispatch(
      setLeadershipInformation({
        ...data,
        leadershipInfoIsValid: true,
      })
    );
    nextHandler();
  };

  const onSubmit = (data: any) => {
    setUploadError('');

    const formIsValid = checkFormValidity();

    if (formIsValid) {
      if (data.selectedFiles?.length) {
        const signAssetPayload = data.selectedFiles.map((file: File) => ({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        }));
        uploadFile.mutate({
          signAssetPayload,
          files: data.selectedFiles,
          formState: data,
        });
      } else {
        updateState(data);
      }
    } else {
      onError();
    }
  };

  const onError = () => {
    setFormErrors('Form is invalid. Please fix the highlighted form fields.');
    dispatch(setLeadershipInformation({ leadershipInfoIsValid: false }));
  };

  const onPreviousHandler = () => {
    const formIsValid = checkFormValidity();
    if (formIsValid) {
      dispatch(
        setLeadershipInformation({
          racialEthnicGroup: getValues('racialEthnicGroup'),
          majorityOwnership: getValues('majorityOwnership'),
          leadershipInfoIsValid: true,
        })
      );
      previousHandler();
    } else {
      onError();
    }
  };

  return (
    <div className='space-y-6 sm:px-6 lg:px-0 lg:col-span-8'>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className='sm:rounded-r-md sm:overflow-hidden'>
          <div className='py-6 px-4 space-y-6 sm:p-6'>
            <div>
              <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>
                Leadership Information
              </h3>
              <p className='mt-1 text-sm text-gray-500'>
                Identify leadership of the company
              </p>
            </div>
            <div className='grid grid-cols-6 gap-6'>
              <h4 className='leading-6 font-medium text-gray-900 col-span-6'>
                Identify the ownership breakdown of the company
              </h4>

              <div className='leading-6 font-medium text-gray-900 col-span-6'>
                <CheckboxField
                  label='What racial/ethnic groups does the highest authority member of
                  management (President, CEO, Partner, etc) belong to? Select
                  all that apply:'
                  name='racialEthnicGroup'
                  fieldOptions={racialEthinicGroup}
                  register={register}
                />
              </div>
              <div className='leading-6 font-medium text-gray-900 col-span-6'>
                <CheckboxField
                  label='Is the majority ownership (51% or greater) of the company held
                  by someone that is, (check all that apply):'
                  name='majorityOwnership'
                  fieldOptions={majorityOwnership}
                  register={register}
                />
              </div>
              <div className='leading-6 font-medium text-gray-900 col-span-6'>
                <label
                  htmlFor='developmentCostEstimate'
                  className='block font-medium text-gray-900'
                >
                  If the company is governed by a board of directors, please
                  specify the percentage of the borad that identifies with any
                  of the groups below:
                </label>
                <fieldset className='space-y-2 mt-2'>
                  <div className='ml-3 text-sm flex w-full gap-2'>
                    <label
                      htmlFor='boardCommunityMembers'
                      className='font-medium text-gray-700'
                    >
                      Community Members
                    </label>
                    <input
                      type='number'
                      name='communityMembersPercent'
                      id='communityMembersPercent'
                      autoComplete='communityMembersPercent'
                      className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                      value={boardOfDirectorsPercent.communityMembersPercent}
                      onChange={bodInputChangeHandler}
                    />
                    <span>%</span>
                  </div>
                  <div className='ml-3 text-sm flex w-full gap-2'>
                    <label
                      htmlFor='boardHispanicLatinx'
                      className='font-medium text-gray-700'
                    >
                      Hispanic/Latinx
                    </label>
                    <input
                      type='number'
                      name='hispanicLatinxPercent'
                      id='hispanicLatinxPercent'
                      autoComplete='hispanicLatinxPercent'
                      className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                      value={boardOfDirectorsPercent.hispanicLatinxPercent}
                      onChange={bodInputChangeHandler}
                    />
                    <span>%</span>
                  </div>

                  <div className='ml-3 text-sm flex w-full gap-2'>
                    <label
                      htmlFor='boardBlackAfricanAmerican'
                      className='font-medium text-gray-700'
                    >
                      Black/African American (not Hispanic or Latinx)
                    </label>
                    <input
                      type='number'
                      name='blackAfricanAmericanPercent'
                      id='blackAfricanAmericanPercent'
                      autoComplete='blackAfricanAmericanPercent'
                      className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                      value={
                        leadershipInformation.boardOfDirectorsPercent
                          .blackAfricanAmericanPercent
                      }
                      onChange={bodInputChangeHandler}
                    />
                    <span>%</span>
                  </div>
                  <div className='ml-3 text-sm flex w-full gap-2'>
                    <label
                      htmlFor='boardAsianPacificIslanders'
                      className='font-medium text-gray-700'
                    >
                      Asian American/Pacific Islanders/Asian (not Hispanic or
                      Latinx)
                    </label>
                    <input
                      type='number'
                      name='asianPacificIslandersPercent'
                      id='asianPacificIslandersPercent'
                      autoComplete='asianPacificIslandersPercent'
                      className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                      value={
                        leadershipInformation.boardOfDirectorsPercent
                          .asianPacificIslandersPercent
                      }
                      onChange={bodInputChangeHandler}
                    />
                    <span>%</span>
                  </div>
                  <div className='ml-3 text-sm flex w-full gap-2'>
                    <label
                      htmlFor='boardNativeAmerican'
                      className='font-medium text-gray-700'
                    >
                      Native American/American Indian/Alaska Native/Indegenous
                      (not Hispanic or Latinx)
                    </label>
                    <input
                      type='number'
                      name='nativeAmericanPercent'
                      id='nativeAmericanPercent'
                      autoComplete='nativeAmericanPercent'
                      className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                      value={
                        leadershipInformation.boardOfDirectorsPercent
                          .nativeAmericanPercent
                      }
                      onChange={bodInputChangeHandler}
                    />
                    <span>%</span>
                  </div>
                  <div className='ml-3 text-sm flex w-full gap-2'>
                    <label
                      htmlFor='boardWhiteCaucasian'
                      className='font-medium text-gray-700'
                    >
                      White/Caucasian (not Hispanic or Latinx)
                    </label>
                    <input
                      type='number'
                      name='whiteCaucasianPercent'
                      id='whiteCaucasianPercent'
                      autoComplete='whiteCaucasianPercent'
                      className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                      value={
                        leadershipInformation.boardOfDirectorsPercent
                          .whiteCaucasianPercent
                      }
                      onChange={bodInputChangeHandler}
                    />
                    <span>%</span>
                  </div>
                  <div className='ml-3 text-sm flex w-full gap-2'>
                    <label
                      htmlFor='boardMultiRacial'
                      className='font-medium text-gray-700'
                    >
                      Multi-Racial, 2+ races
                    </label>
                    <input
                      type='number'
                      name='multiRacialPercent'
                      id='multiRacialPercent'
                      autoComplete='multiRacialPercent'
                      className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                      value={
                        leadershipInformation.boardOfDirectorsPercent
                          .multiRacialPercent
                      }
                      onChange={bodInputChangeHandler}
                    />
                    <span>%</span>
                  </div>
                  <p className='text-tertiaryColor-normal text-md'>
                    {bodErrors}
                  </p>
                  {bodTotalPercent(boardOfDirectorsPercent) > 100 && (
                    <p className='text-tertiaryColor-normal text-md'>
                      Total percent cannot be greater than 100%
                    </p>
                  )}
                </fieldset>
              </div>
            </div>
            <div className='col-span-6'>
              <FileInputField
                name='selectedFiles'
                label='Principal Resume'
                id='selectedFiles'
                placeholder='Upload Principal Resume'
                multiple
                {...{ control, watch, setValue, trigger }}
              />
            </div>
            {uploadError && (
              <p className='text-tertiaryColor-normal text-md col-span-6'>
                {uploadError}
              </p>
            )}
            <p className='text-tertiaryColor-normal text-md'>{formErrors}</p>
            <div className='flex gap-4 justify-end mt-5'>
              <Button
                label='Previous'
                disabled={uploadFile.isLoading}
                btnStyle='btn-neutral'
                type='button'
                onClick={onPreviousHandler}
              />
              <Button
                label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
                btnStyle={uploadFile?.isLoading ? 'btn-neutral' : 'btn-primary'}
                disabled={uploadFile.isLoading}
                type='submit'
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
